// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { ShippingCarrierAutocomplete } from '@/components/shipping-carrier-autocomplete';
import {
  SHIPPING_CARRIERS,
  ShippingCarrier,
  compareShippingCarriers,
  isPrimaryShippingCarrier,
} from '@/constants/shipping-carrier';
import { ForwardedRef, forwardRef } from 'react';

interface Props {
  /**
   * The name of the form input element.
   */
  name?: string;

  /**
   * The label attached to the input element.
   */
  label?: string;

  /**
   * If true, the component will be displayed in an error state.
   */
  error?: boolean;

  /**
   * If provided, the helper text will be displayed below the input element.
   */
  helperText?: string;

  /**
   * The currently selected carrier.
   */
  value: ShippingCarrier | null;

  /**
   * A callback fired when the selected carrier changes.
   */
  onChange: (value: ShippingCarrier | null) => void;

  /**
   * If true, the input is disabled.
   */
  disabled?: boolean;
}

/**
 * Renders a carrier autocomplete component with a list of shipping carriers.
 * This function also forwards a ref to the autocomplete component.
 */
function ShippingCarrierSearchRenderer(
  props: Props,
  ref: ForwardedRef<HTMLInputElement>
) {
  const options = SHIPPING_CARRIERS.slice().sort(compareShippingCarriers);

  const handleChange = (_: unknown, value: ShippingCarrier | null) => {
    props.onChange(value);
  };

  return (
    <ShippingCarrierAutocomplete
      ref={ref}
      size="small"
      name={props.name}
      value={props.value}
      error={props.error}
      label={props.label}
      groupBy={(option) =>
        isPrimaryShippingCarrier(option)
          ? 'Major carriers'
          : 'All other carriers'
      }
      options={options}
      onChange={handleChange}
      helperText={props.helperText}
      disabled={props.disabled}
    />
  );
}

/**
 * A controlled component for selecting a carrier from a list of options.
 */
export const ShippingCarrierSearch = forwardRef(ShippingCarrierSearchRenderer);
